const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://landingpagecreatorbackend.azurewebsites.net/api';

export const websitesAPI = {
  getStorageTargets: async () => {
    const response = await fetch(`${API_BASE_URL}/storage-targets`);
    if (!response.ok) {
      throw new Error('Failed to fetch storage targets');
    }
    return response.json();
  },

  // Get all websites
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/websites`);
    if (!response.ok) {
      throw new Error('Failed to fetch websites');
    }
    return response.json();
  },

  // Get a specific website
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/websites/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch website');
    }
    return response.json();
  },

  // Create a new website
  create: async (name, description = '') => {
    const response = await fetch(`${API_BASE_URL}/websites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, description }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create website');
    }
    return response.json();
  },

  // Update a website (pass only fields to change; name and description optional)
  update: async (id, { name, description, storageTargetId, globalCss } = {}) => {
    const body = {};
    if (name !== undefined) body.name = name;
    if (description !== undefined) body.description = description;
    if (storageTargetId !== undefined) body.storageTargetId = storageTargetId;
    if (globalCss !== undefined) body.globalCss = globalCss;
    const response = await fetch(`${API_BASE_URL}/websites/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update website');
    }
    return response.json();
  },

  // Delete a website
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/websites/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete website');
    }
    return true;
  },

  // Get HTML content for a specific website and audience
  getContent: async (websiteId, audienceId) => {
    const response = await fetch(`${API_BASE_URL}/websites/${websiteId}/content/${audienceId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch content');
    }
    return response.json();
  },

  // Save HTML content for a specific website and audience
  saveContent: async (websiteId, audienceId, html, css, components) => {
    const response = await fetch(`${API_BASE_URL}/websites/${websiteId}/content/${audienceId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ html, css, components }),
    });
    
    // Check content type before parsing
    const contentType = response.headers.get('content-type');
    if (!response.ok) {
      let errorMessage = 'Failed to save content';
      try {
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
        } else {
          // If not JSON, try to get text
          const text = await response.text();
          errorMessage = text || errorMessage;
        }
      } catch (e) {
        // If parsing fails, use status text
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    // Only parse as JSON if content type is JSON
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    } else {
      // If not JSON, return the text response
      const text = await response.text();
      return { success: true, message: text || 'Content saved successfully' };
    }
  },

  // Get all content for a website
  getAllContent: async (websiteId) => {
    const response = await fetch(`${API_BASE_URL}/websites/${websiteId}/content`);
    if (!response.ok) {
      throw new Error('Failed to fetch website content');
    }
    return response.json();
  },

  // Get blob storage URLs for all audiences of a website
  getBlobUrls: async (websiteId) => {
    const response = await fetch(`${API_BASE_URL}/websites/${websiteId}/blob-urls`);
    if (!response.ok) {
      throw new Error('Failed to fetch blob URLs');
    }
    return response.json();
  },

  // Get version history for a website and audience
  getVersions: async (websiteId, audienceId) => {
    const response = await fetch(`${API_BASE_URL}/websites/${websiteId}/content/${audienceId}/versions`);
    if (!response.ok) {
      throw new Error('Failed to fetch versions');
    }
    return response.json();
  },

  // Restore a version for a website and audience
  restoreVersion: async (websiteId, audienceId, version) => {
    const response = await fetch(`${API_BASE_URL}/websites/${websiteId}/content/${audienceId}/versions/${version}/restore`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to restore version');
    }
    return response.json();
  },

  // Get stats for an audience (logs + visits over time)
  getStats: async (websiteId, audienceId) => {
    const response = await fetch(`${API_BASE_URL}/websites/${websiteId}/audiences/${audienceId}/stats`);
    if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }
    return response.json();
  },
};

